import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * AccountMenu
 *
 * The `@username` pill in the AppNav trailing cluster IS the trigger
 * for the account-management popover. Clicking it opens a small menu
 * with three rows — Username, Email, Phone — each showing the
 * current value (or `Add`) and opening its own modal on click.
 *
 * Replaces the previous arrangement of a separate gear icon + standalone
 * `@username` button: a user looking for "how do I set my email" can
 * find it from the same affordance that already shows their handle.
 */
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/auth/auth-context";
import { useAccountMe } from "@/hooks/use-account-me";
import { UsernameClaimModal } from "@/components/username-claim-modal/username-claim-modal";
import { AccountIdentifierModal } from "@/components/account-identifier-modal/account-identifier-modal";
import "./account-menu.scss";
export const AccountMenu = memo(() => {
    const accountMe = useAccountMe();
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [modal, setModal] = useState();
    const wrapperRef = useRef(null);
    // Close on click outside; ignore clicks on the trigger itself so its
    // toggle behavior doesn't fight the outside-close.
    useEffect(() => {
        if (!open)
            return;
        function onPointerDown(e) {
            const wrapper = wrapperRef.current;
            if (!wrapper)
                return;
            if (wrapper.contains(e.target))
                return;
            setOpen(false);
        }
        window.addEventListener("pointerdown", onPointerDown);
        return () => window.removeEventListener("pointerdown", onPointerDown);
    }, [open]);
    // Esc closes the popover.
    useEffect(() => {
        if (!open)
            return;
        function onKeyDown(e) {
            if (e.key === "Escape")
                setOpen(false);
        }
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [open]);
    const openModal = useCallback((kind) => {
        setOpen(false);
        setModal(kind);
    }, []);
    if (accountMe.isLoading)
        return null;
    const username = accountMe.account?.username;
    const triggerLabel = username ? `@${username}` : "Set username";
    const phone = user?.phone;
    const email = user?.email;
    return (_jsxs("div", { className: "account-menu", ref: wrapperRef, children: [_jsx("button", { type: "button", className: "account-menu__trigger", "aria-haspopup": "menu", "aria-expanded": open, "aria-label": username ? `Account: @${username}` : "Set username and account info", title: triggerLabel, onClick: () => setOpen((v) => !v), children: _jsx("span", { className: "account-menu__trigger-label", children: triggerLabel }) }), open ? (_jsxs("div", { className: "account-menu__popover", role: "menu", children: [_jsx("div", { className: "account-menu__section-label", children: "Account" }), _jsxs("button", { type: "button", role: "menuitem", className: "account-menu__item", onClick: () => openModal("username"), children: [_jsx("span", { className: "account-menu__item-label", children: "Username" }), _jsx("span", { className: "account-menu__item-value", children: username ? `@${username}` : _jsx("em", { children: "Add" }) })] }), _jsxs("button", { type: "button", role: "menuitem", className: "account-menu__item", onClick: () => openModal("phone"), children: [_jsx("span", { className: "account-menu__item-label", children: "Phone" }), _jsx("span", { className: "account-menu__item-value", children: phone ? phone : _jsx("em", { children: "Add" }) })] }), _jsxs("button", { type: "button", role: "menuitem", className: "account-menu__item", onClick: () => openModal("email"), children: [_jsx("span", { className: "account-menu__item-label", children: "Email" }), _jsx("span", { className: "account-menu__item-value", children: email ? email : _jsx("em", { children: "Add" }) })] })] })) : null, modal === "username" ? (_jsx(UsernameClaimModal, { account: accountMe.account, onClose: () => setModal(undefined) })) : null, modal === "phone" || modal === "email" ? (_jsx(AccountIdentifierModal, { type: modal, onClose: () => setModal(undefined) })) : null] }));
});
